const PLAY_VALUES = new Set(['true', '1', 'yes', 'on']);
const MUTE_VALUES = new Set(['false', '0', 'no', 'off', 'undefined', 'null']);

function isProvidedCustomSound(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

/**
 * FCM `data.payload` arrives as a JSON string, e.g.
 * `"{\"custom_sound\":true,\"type\":\"ENQUIRY\"}"`.
 * Parse (including one level of double-encoding) into an object.
 */
export function parseNotificationPayload(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== 'string') return {};

  let current: unknown = raw;
  // FCM / backends sometimes double-stringify the payload.
  for (let i = 0; i < 2; i++) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) return {};
    try {
      current = JSON.parse(trimmed);
    } catch {
      return {};
    }
  }

  if (current && typeof current === 'object' && !Array.isArray(current)) {
    return current as Record<string, unknown>;
  }
  return {};
}

/**
 * Coerce nested `custom_sound` (boolean | string | number) to a real boolean.
 * Only explicit true-like values play sound.
 */
export function coerceCustomSoundFlag(value: unknown): boolean {
  if (!isProvidedCustomSound(value)) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (MUTE_VALUES.has(normalized)) return false;
  return PLAY_VALUES.has(normalized);
}

/** Nested payload only. Outer data.custom_sound is ignored. */
export function shouldPlayCustomSound(
  payloadData: Record<string, unknown> | null | undefined,
): boolean {
  return coerceCustomSoundFlag(payloadData?.['custom_sound']);
}

/**
 * Parse `data.payload` string first, then evaluate nested `custom_sound` as boolean.
 */
export function resolveCustomSoundFromPushData(
  data: Record<string, unknown> | null | undefined,
): boolean {
  return shouldPlayCustomSound(parseNotificationPayload(data?.['payload']));
}
